import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { AlertTriangle, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../config/api";

import { errorMessage } from "../utils/errorMessage";
export default function MatterAlertsPanel({ matterReference }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    if (!matterReference) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const headers = {};
      const userId = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id");
      if (userId) headers["X-Bonsai-User-Id"] = userId;
      const res = await axios.get(
        `${API}/mate/alerts`,
        { params: { status: "active", matter_reference: matterReference }, headers }
      );
      setAlerts(res?.data?.alerts || []);
    } catch (err) {
      console.error("[MatterAlertsPanel] fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, [matterReference]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleDismiss = async (alertId) => {
    try {
      const headers = {};
      const userId = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id");
      if (userId) headers["X-Bonsai-User-Id"] = userId;
      await axios.post(`${API}/mate/alerts/${alertId}/dismiss`, {}, { headers });
      toast.success("Alert dismissed");
      fetchAlerts();
    } catch (err) {
      toast.error(errorMessage(err, "Dismiss failed"));
    }
  };

  if (loading) {
    return (
      <div className="mt-4 text-sm text-slate-500" data-testid="matter-alerts-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> Loading alerts...
      </div>
    );
  }
  if (alerts.length === 0) return null;

  return (
    <div className="mt-4 p-4 rounded-md border border-amber-300/40 bg-amber-50/40" data-testid="matter-alerts-panel">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <h3 className="font-medium text-amber-900">Mate alerts for this matter</h3>
        <span className="text-xs text-amber-700">({alerts.length})</span>
      </div>
      <ul className="space-y-2">
        {alerts.map((alert) => (
          <li key={alert.id} className="flex items-start justify-between gap-2 p-2 rounded bg-white/60">
            <div className="text-sm">
              <div className="font-medium text-amber-900">{alert.title || alert.alert_type}</div>
              <div className="text-slate-700">{alert.detail}</div>
            </div>
            <button
              type="button"
              onClick={() => handleDismiss(alert.id)}
              className="text-slate-400 hover:text-slate-700"
              aria-label="Dismiss alert"
              data-testid={`dismiss-alert-${alert.id}`}
            >
              <X className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
